class EventCalendar {
  constructor(section) {
    this.section = section;

    this.cards = Array.from(
      section.querySelectorAll('[data-event-card]')
    );

    this.upcomingGrid = section.querySelector('[data-upcoming-grid]');
    this.pastGrid = section.querySelector('[data-past-grid]');

    this.tabs = Array.from(
      section.querySelectorAll('[data-calendar-tab]')
    );

    this.panels = Array.from(
      section.querySelectorAll('[data-calendar-panel]')
    );

    this.monthFilter = section.querySelector(
      '[data-event-filter="month"]'
    );

    this.locationFilter = section.querySelector(
      '[data-event-filter="location"]'
    );

    this.brandFilter = section.querySelector(
      '[data-event-filter="brand"]'
    );

    this.clearButton = section.querySelector('[data-clear-filters]');

    this.upcomingCount = section.querySelector('[data-upcoming-count]');
    this.pastCount = section.querySelector('[data-past-count]');

    this.activeTab = 'upcoming';

    this.upcomingCards = [];
    this.pastCards = [];

    this.init();
  }

  init() {
    this.separateEvents();
    this.updateCounts();
    this.updateFilterOptions();
    this.bindEvents();
    this.applyFilters();
  }

  getToday() {
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  separateEvents() {
    const today = this.getToday();

    this.upcomingCards = [];
    this.pastCards = [];

    this.cards.forEach((card) => {
      const startDate = card.dataset.startDate || '';
      const endDate = card.dataset.endDate || startDate;

      /*
       * An event remains upcoming until its end date has passed.
       * Single-day events use the start date as their end date.
       */
      if (endDate >= today) {
        this.upcomingCards.push(card);
        this.upcomingGrid?.appendChild(card);
      } else {
        this.pastCards.push(card);
        this.pastGrid?.appendChild(card);
      }
    });

    this.sortCards(this.upcomingCards, 'ascending');
    this.sortCards(this.pastCards, 'descending');
  }

  sortCards(cards, direction = 'ascending') {
    cards.sort((firstCard, secondCard) => {
      const firstDate = firstCard.dataset.startDate || '';
      const secondDate = secondCard.dataset.startDate || '';

      if (direction === 'descending') {
        return secondDate.localeCompare(firstDate);
      }

      return firstDate.localeCompare(secondDate);
    });

    cards.forEach((card) => {
      const grid =
        direction === 'descending'
          ? this.pastGrid
          : this.upcomingGrid;

      grid?.appendChild(card);
    });
  }

  getActiveCards() {
    return this.activeTab === 'past'
      ? this.pastCards
      : this.upcomingCards;
  }

  getSelectedFilters() {
    return {
      month: this.monthFilter?.value || '',
      location: this.locationFilter?.value || '',
      brand: this.brandFilter?.value || ''
    };
  }

  cardMatches(card, filters, ignoredFilter = '') {
    const cardMonth = card.dataset.month || '';
    const cardLocation = card.dataset.location || '';
    const cardBrand = card.dataset.brand || '';

    const matchesMonth =
      ignoredFilter === 'month' ||
      !filters.month ||
      cardMonth === filters.month;

    const matchesLocation =
      ignoredFilter === 'location' ||
      !filters.location ||
      cardLocation === filters.location;

    const matchesBrand =
      ignoredFilter === 'brand' ||
      !filters.brand ||
      cardBrand === filters.brand;

    return matchesMonth && matchesLocation && matchesBrand;
  }

  updateFilterOptions() {
    const cards = this.getActiveCards();
    const filters = this.getSelectedFilters();

    /*
     * Each dropdown is generated using the other selected filters.
     * Example: Location uses the selected Month and Brand.
     */
    const monthCards = cards.filter((card) =>
      this.cardMatches(card, filters, 'month')
    );

    const locationCards = cards.filter((card) =>
      this.cardMatches(card, filters, 'location')
    );

    const brandCards = cards.filter((card) =>
      this.cardMatches(card, filters, 'brand')
    );

    this.populateSelect(
      this.monthFilter,
      monthCards,
      'month',
      'All months',
      this.formatMonth.bind(this)
    );

    /*
     * Month selection is now applied here, so only locations from
     * the selected month are displayed.
     */
    const refreshedFilters = this.getSelectedFilters();

    const refreshedLocationCards = cards.filter((card) =>
      this.cardMatches(card, refreshedFilters, 'location')
    );

    this.populateSelect(
      this.locationFilter,
      refreshedLocationCards,
      'location',
      'All locations'
    );

    const finalFilters = this.getSelectedFilters();

    const refreshedBrandCards = cards.filter((card) =>
      this.cardMatches(card, finalFilters, 'brand')
    );

    this.populateSelect(
      this.brandFilter,
      refreshedBrandCards,
      'brand',
      'All brands'
    );
  }

  populateSelect(
    select,
    cards,
    dataKey,
    defaultLabel,
    labelFormatter = null
  ) {
    if (!select) return;

    const previousValue = select.value;

    const values = [
      ...new Set(
        cards
          .map((card) => card.dataset[dataKey]?.trim())
          .filter(Boolean)
      )
    ];

    values.sort((firstValue, secondValue) => {
      if (dataKey === 'month') {
        return firstValue.localeCompare(secondValue);
      }

      return firstValue.localeCompare(
        secondValue,
        undefined,
        { sensitivity: 'base' }
      );
    });

    select.innerHTML = '';

    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = defaultLabel;
    select.appendChild(defaultOption);

    values.forEach((value) => {
      const option = document.createElement('option');

      option.value = value;
      option.textContent = labelFormatter
        ? labelFormatter(value)
        : value;

      select.appendChild(option);
    });

    /*
     * Keep the selected option only if it is still available.
     * Otherwise reset the dropdown.
     */
    if (values.includes(previousValue)) {
      select.value = previousValue;
    } else {
      select.value = '';
    }
  }

  formatMonth(value) {
    if (!value) return '';

    const [year, month] = value.split('-');

    const date = new Date(
      Number(year),
      Number(month) - 1,
      1
    );

    return date.toLocaleDateString('en-AU', {
      month: 'long',
      year: 'numeric'
    });
  }

  applyFilters() {
    const filters = this.getSelectedFilters();
    const activeCards = this.getActiveCards();

    let visibleCount = 0;

    this.cards.forEach((card) => {
      card.hidden = true;
    });

    activeCards.forEach((card) => {
      const shouldShow = this.cardMatches(card, filters);

      card.hidden = !shouldShow;

      if (shouldShow) {
        visibleCount += 1;
      }
    });

    this.updateEmptyState(visibleCount);
  }

  updateEmptyState(visibleCount) {
    const upcomingEmpty = this.section.querySelector(
      '[data-empty-state="upcoming"]'
    );

    const pastEmpty = this.section.querySelector(
      '[data-empty-state="past"]'
    );

    if (upcomingEmpty) {
      upcomingEmpty.hidden =
        this.activeTab !== 'upcoming' ||
        visibleCount > 0;
    }

    if (pastEmpty) {
      pastEmpty.hidden =
        this.activeTab !== 'past' ||
        visibleCount > 0;
    }
  }

  updateCounts() {
  if (this.upcomingCount) {
    this.upcomingCount.textContent =
      this.upcomingCards.length;
  }

  if (this.pastCount) {
    this.pastCount.textContent =
      this.pastCards.length;
  }
}

  changeTab(tabName) {
    this.activeTab = tabName;

    this.tabs.forEach((tab) => {
      const isActive =
        tab.dataset.calendarTab === tabName;

      tab.classList.toggle('is-active', isActive);
      tab.setAttribute(
        'aria-selected',
        isActive ? 'true' : 'false'
      );
    });

    this.panels.forEach((panel) => {
      const isActive =
        panel.dataset.calendarPanel === tabName;

      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    this.resetFilters();
  }

  resetFilters() {
    if (this.monthFilter) {
      this.monthFilter.value = '';
    }

    if (this.locationFilter) {
      this.locationFilter.value = '';
    }

    if (this.brandFilter) {
      this.brandFilter.value = '';
    }

    this.updateFilterOptions();
    this.applyFilters();
  }

  handleFilterChange(changedFilter) {
    /*
     * When Month changes, reset Location and Brand because their
     * previous values may not exist in the new month.
     */
    if (changedFilter === 'month') {
      if (this.locationFilter) {
        this.locationFilter.value = '';
      }

      if (this.brandFilter) {
        this.brandFilter.value = '';
      }
    }

    /*
     * When Location changes, reset Brand because available brands
     * may have changed.
     */
    if (changedFilter === 'location') {
      if (this.brandFilter) {
        this.brandFilter.value = '';
      }
    }

    this.updateFilterOptions();
    this.applyFilters();
  }

  bindEvents() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        this.changeTab(tab.dataset.calendarTab);
      });
    });

    this.monthFilter?.addEventListener('change', () => {
      this.handleFilterChange('month');
    });

    this.locationFilter?.addEventListener('change', () => {
      this.handleFilterChange('location');
    });

    this.brandFilter?.addEventListener('change', () => {
      this.handleFilterChange('brand');
    });

    this.clearButton?.addEventListener('click', () => {
      this.resetFilters();
    });
  }
}

function initialiseEventCalendars(container = document) {
  container
    .querySelectorAll('[data-event-calendar]')
    .forEach((section) => {
      if (section.dataset.calendarInitialised === 'true') {
        return;
      }

      section.dataset.calendarInitialised = 'true';
      new EventCalendar(section);
    });
}

document.addEventListener('DOMContentLoaded', () => {
  initialiseEventCalendars();
});

/*
 * Required for Shopify Theme Editor when the section reloads.
 */
document.addEventListener('shopify:section:load', (event) => {
  initialiseEventCalendars(event.target);
});